import { CommonModule, DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup, FormsModule, NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatDialog } from '@angular/material/dialog';
import { AccountModel, CustomerInformation, CustomerSearchType, EstimateInfoForOpportunity, EstimateTypes, FFDropDownCollectionResponse, OpportunityModel, OpportunityRequest, OtherMetaData } from '../../../../models/dtos';
import { CreateOpportunityEvent } from '../../../../services/api/crm-api.service';
import { SearchCustomersDialogComponent } from '../search-customers-dialog/search-customers-dialog.component';
import { ISearchCustomersData } from '../../../../models/common/ISearchCustomersData';
import { ICreateOpportunityFormGroup } from '../../../../models/formGroupInterfaces/ICreateOpportunityFormGroup';
import { CrmOperationsService } from '../../../../services/crm-operations.service';
import { IRadioButtonState } from '../../../../models/common/IRadioButtonState';
import { ISearchVerifyButtonState } from '../../../../models/common/ISearchVerifyButtonState';
import { ICmmcState } from '../../../../models/common/ICmmcState';
import { IFederalFundedOutputModel } from '../../../../models/common/IFederalFundedOutputModel';
import { IVerificationState } from '../../../../models/common/IVerificationState';
import { MultilingualCurrencyPipe } from '../../../../pipes/multilingual-currency-pipe/multilingual-currency.pipe';
import { firstValueFrom, finalize, merge, Subject, takeUntil } from 'rxjs';
import { SearchEstimateDialogComponent } from '../search-estimate-dialog/search-estimate-dialog.component';
import { SearchOpportunityDialogComponent } from '../search-opportunity-dialog/search-opportunity-dialog.component';
import { CONSTANTS } from '../../../../Constants/Constants';
import { UserProfileService } from '../../../../services/common/user-profile.service';
import { DateAdapter, MAT_DATE_FORMATS, MAT_NATIVE_DATE_FORMATS } from '@angular/material/core';
import { MultilingualDateAdapter } from '../../../../adapters/multilingual-date.adapter';
import { MatCheckbox } from '@angular/material/checkbox';
import { CommonDataService } from '../../../../services/common-data.service';
import { CRMEntityDetails } from '../../../../models/common/CRMEntityDetails';
import { CrmEntityDetailsPopupComponent } from '../crm-entity-details-popup/crm-entity-details-popup';
import { NotificationMessageComponent } from '../notification-message/notification-message.component';
import { NotificationMessageService } from '../../../../services/common/notification-message.service';
import { MatIconModule } from '@angular/material/icon';
import { MultilingualDatePipe } from '../../../../pipes/multilingual-date-pipe/multilingual-date-pipe';
import { GocCollaborationRestrictionHandlerService } from '../../../../services/common/goc-collaboration-restriction-handler.service';
import { LoadingSpinnerService } from '../../../../services/common/loading-spinner.service';
import { EstimateStatus } from '../../../../models/dtos/Enums/EstimateStatus';

@Component({
  selector: 'app-create-opportunity',
  imports: [
    MatFormFieldModule,
    MatButtonModule,
    MatInputModule,
    MatDatepickerModule,
    MatRadioModule,
    MatSelectModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslatePipe,
    MultilingualCurrencyPipe,
    MatCheckbox,
    CrmEntityDetailsPopupComponent,
    MatIconModule,
  ],
  templateUrl: './create-opportunity.component.html',
  styleUrl: './create-opportunity.component.scss',
  providers: [{ provide: DateAdapter, useClass: MultilingualDateAdapter }, { provide: MAT_DATE_FORMATS, useValue: MAT_NATIVE_DATE_FORMATS }, DatePipe, MultilingualDatePipe]
})
export class CreateOpportunityComponent implements OnInit, OnDestroy {

  @Output() cancel = new EventEmitter<void>();

  private _isCollaborativeReadOnly = false;
  @Input()
  set isCollaborativeReadOnly(value: boolean) {
    this._isCollaborativeReadOnly = value;
    this.applyCollaborativeReadOnlyState();
  }
  get isCollaborativeReadOnly(): boolean {
    return this._isCollaborativeReadOnly;
  }

  public createOpportunityFormGroup!: FormGroup<ICreateOpportunityFormGroup>;
  private collaborativeControlDisabledStates: Map<keyof ICreateOpportunityFormGroup, boolean> | null = null;
  private collaborativePreviousFormDisabled: boolean | null = null;

  private isApplyingSharedData = false;
  cultureId: number = CONSTANTS.ONE;

  // Expose enum to template
  readonly CustomerSearchType = CustomerSearchType;

  // ─── Federal-funded dropdown items ───
  entityItems: Array<{ label: string; value: string }> = [];
  agencyItems: Array<{ label: string; value: string }> = [];
  contractScheduleItems: Array<{ label: string; value: string }> = [];

  // ─── Federal-funded control states ───
  buyAmericanState: IRadioButtonState = { yesEnabled: true, noEnabled: true, checked: null };
  dpasState: IRadioButtonState = { yesEnabled: true, noEnabled: true, checked: null };
  cmmcState: ICmmcState = { cuiEnabled: true, fciEnabled: true, checked: null };

  // ─── Restriction type items ───
  restrictionTypeItems: string[] = [];

  // ─── Model output for API ───
  federalFundedOutput: IFederalFundedOutputModel = {
    govtEntity: null,
    govtAgency: null,
    contractSchedule: null,
    buyAmerican: null,
    dpas: null,
    cmmcLevel: null,
    restrictionType: null,
  };

  // ─── Customer data ───
  accountNameData = new CustomerInformation();
  shipToLocationData = new CustomerInformation();
  billToData = new CustomerInformation();
  endUserData = new CustomerInformation();

  customerButtonStates = {
    accountName: { searchDisabled: false, verifyDisabled: true },
    shipToLocation: { searchDisabled: false, verifyDisabled: true },
    billTO: { searchDisabled: false, verifyDisabled: true },
    endUser: { searchDisabled: false, verifyDisabled: true }
  };

  isFormDisabled = false;
  isChangeOrder = false;
  isCreating = false;
  operationStatus: string | null = null;
  baseOpportunityDisabled = true;
  baseOpportunityButtonStates: ISearchVerifyButtonState = { searchDisabled: false, verifyDisabled: false };
  customerAccountsDisabled = false;
  estimateButtonStates: ISearchVerifyButtonState = { searchDisabled: false, verifyDisabled: false };

  // ─── Verification tracking ───
  verifiedFields: IVerificationState = {
    estimateId: false,
    accountName: false,
    shipToLocation: false,
    billTO: false,
    endUser: false
  };
  applySoldToSection: Record<string, boolean> = {
    [CONSTANTS.SHIP_TO_KEY]: false,
    [CONSTANTS.BILL_TO_KEY]: false,
    [CONSTANTS.END_USER_KEY]: false
  };

  estimateInfoForOpportunity!: EstimateInfoForOpportunity;
  opportunityModel!: OpportunityModel;
  private initialEstimateInfo!: EstimateInfoForOpportunity;
  private usInfoDDLists!: FFDropDownCollectionResponse;
  private readonly destroy$ = new Subject<void>();
  private readonly resubscribe$ = new Subject<void>();

  constructor(
    private readonly crmOperations: CrmOperationsService,
    private readonly fb: NonNullableFormBuilder,
    private readonly dialog: MatDialog,
    private readonly userProfileService: UserProfileService,
    private readonly commonDataService: CommonDataService,
    private readonly notificationService: NotificationMessageService,
    private readonly translate: TranslateService,
    private readonly multilingualDatePipe: MultilingualDatePipe,
    private readonly gocCollaborationRestrictionHandlerService: GocCollaborationRestrictionHandlerService,
    private readonly loadingSpinnerService: LoadingSpinnerService
  ) { }

  // #region ─── Public Methods ───

  get salesOffice(): FormControl<string> {
    return this.createOpportunityFormGroup.controls.salesOffice;
  }

  get businessLine(): FormControl<string> {
    return this.createOpportunityFormGroup.controls.businessLine;
  }

  get segment(): FormControl<string> {
    return this.createOpportunityFormGroup.controls.segment;
  }

  get businessHierarchy(): FormControl<string> {
    return this.createOpportunityFormGroup.controls.businessHierarchy;
  }

  get sellAs(): FormControl<string> {
    return this.createOpportunityFormGroup.controls.sellAs;
  }

  get canShowFederalFundedSection(): boolean {
    return !this.isChangeOrder && this.cultureId === CONSTANTS.ONE
  }

  get isBillToApplySoldToLocked(): boolean {
    return this.cultureId > CONSTANTS.ONE;
  }

  get isEstimateIdDisabled(): boolean {
    return this.commonDataService.getEstimateId() !== CONSTANTS.DEFAULT_ESTIMATE_ID;
  }

  ngOnInit(): void {
    this.cultureId = this.crmOperations.getCultureId();
    this.initialEstimateInfo = this.crmOperations.getEstimateInfoForOpportunity();
    this.estimateInfoForOpportunity = this.initialEstimateInfo;
    this.applySoldToSection[CONSTANTS.BILL_TO_KEY] = this.isBillToApplySoldToLocked;
    void this.initializeInitialEstimateState();
  }

  ngOnDestroy(): void {
    this.resubscribe$.next();
    this.resubscribe$.complete();
    this.destroy$.next();
    this.destroy$.complete();
  }


  searchEstimate(): void {
    const dialogRef = this.dialog.open(SearchEstimateDialogComponent, {
      width: CONSTANTS.DIALOG_WIDTH_LARGE,
      height: CONSTANTS.DIALOG_HEIGHT_LARGE,
      disableClose: true,
      panelClass: 'search-estimate-dialog'
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((estimateId: string) => {
        if (estimateId) {
          this.createOpportunityFormGroup.controls.estimateId.patchValue(estimateId);
          this.verifyEstimate();
        }
      });
  }

  searchBaseOpportunity(): void {
    const dialogRef = this.dialog.open(SearchOpportunityDialogComponent, {
      width: CONSTANTS.DIALOG_WIDTH_MEDIUM,
      disableClose: true
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((opportunityId: string) => {
        if (opportunityId) {
          this.createOpportunityFormGroup.controls.baseOpportunity.setValue(opportunityId);
          this.verifyBaseOpportunityForCreate(opportunityId);
        }
      });
  }

  verifyBaseOpportunity(): void {
    const opportunityId = this.createOpportunityFormGroup.controls.baseOpportunity.value;
    if (!opportunityId) {
      return;
    }
    this.verifyBaseOpportunityForCreate(opportunityId);
  }

  searchCustomer(searchType: CustomerSearchType): void {
    const dialogRef = this.dialog.open(SearchCustomersDialogComponent, {
      width: CONSTANTS.DIALOG_WIDTH_MEDIUM,
      data: searchType,
      disableClose: true
    });

    dialogRef.afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: { customerInfo: ISearchCustomersData }) => {
        if (result?.customerInfo) {
          const customerInfo = this.crmOperations.convertSearchDataToCustomerInfo(result.customerInfo);
          this.assignCustomerData(searchType, customerInfo);
        }
      });
  }

  verifyCustomer(searchType: CustomerSearchType): void {
    const accountId = this.getCustomerControlValue(searchType);
    if (!accountId) {
      return;
    }

    this.crmOperations.verifyCustomerById(accountId, searchType)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (customerInfo) => {
          if (customerInfo) {
            this.assignCustomerData(searchType, customerInfo);
          } else {
            this.notificationService.showWarning(
              this.translate.instant('CUSTOMER_NOT_FOUND')
            );
          }
        },
        error: () => {
          this.notificationService.showError(
            this.translate.instant('CUSTOMER_VERIFY_FAILED')
          );
        }
      });
  }

  verifyEstimate(): void {
    const estimateId = this.createOpportunityFormGroup.controls.estimateId.value;
    if (!estimateId) {
      return;
    }

    this.crmOperations.verifyEstimateId(estimateId, this.userProfileService.userInfo?.mail ?? '')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (info) => {
          void this.initializeVerifiedEstimateState(info);
        },
        error: async (error) => {
          if (await this.gocCollaborationRestrictionHandlerService.isGocUserRestricted(error)) {
            return;
          }
          this.verifiedFields.estimateId = false;
        }
      });
  }

  get canCreate(): boolean {
    const c = this.createOpportunityFormGroup.controls;
    return this.crmOperations.validateCanCreate({
      isFormDisabled: this.isFormDisabled,
      estimateId: c.estimateId.value,
      accountName: c.accountName.value,
      opportunityName: c.opportunityName.value,
      businessLine: c.businessLine.value,
      salesOffice: c.salesOffice.value,
      segment: c.segment.value,
      businessHierarchy: c.businessHierarchy.value,
      expectedBook: c.expectedBook.value,
      shipToLocation: c.shipToLocation.value,
      endUser: c.endUser.value,
      billTO: c.billTO.value,
      isChangeOrder: this.isChangeOrder,
      baseOpportunityId: c.baseOpportunity.value,
      isBaseOpportunityVerified: this.baseOpportunityButtonStates.verifyDisabled && this.baseOpportunityButtonStates.searchDisabled,
      enabledDropdowns: {
        governmentEntity: c.governmentEntity.enabled,
        governmentAgency: c.governmentAgency.enabled,
        contractSchedule: c.contractSchedule.enabled
      },
      dropdownValues: {
        governmentEntity: c.governmentEntity.value,
        governmentAgency: c.governmentAgency.value,
        contractSchedule: c.contractSchedule.value
      },
      verifiedFields: this.verifiedFields
    });
  }

  cancelChanges(): void {
    // Clear stored open estimate response in service
    this.crmOperations.clearOpenEstimateResponse();

    // Restore to the initial estimate state captured at dialog open
    this.estimateInfoForOpportunity = this.initialEstimateInfo;
    // Tear down all form-bound subscriptions
    this.resubscribe$.next();

    // Rebuild form from initial state
    this.isFormDisabled = false;
    this.initializeFormGroup(this.initialEstimateInfo);
    this.subscribeToCustomerIdChanges();
    this.subscribeToEstimateIdChanges();
    this.applyBaseOpportunityState();

    // Reset customer data
    this.accountNameData = new CustomerInformation();
    this.shipToLocationData = new CustomerInformation();
    this.billToData = new CustomerInformation();
    this.endUserData = new CustomerInformation();

    // Reset checkbox state
    this.applySoldToSection = {
      [CONSTANTS.SHIP_TO_KEY]: false,
      [CONSTANTS.BILL_TO_KEY]: this.isBillToApplySoldToLocked,
      [CONSTANTS.END_USER_KEY]: false
    };

    // Reset button states
    this.customerButtonStates = {
      accountName: { searchDisabled: false, verifyDisabled: true },
      shipToLocation: { searchDisabled: false, verifyDisabled: true },
      billTO: { searchDisabled: false, verifyDisabled: true },
      endUser: { searchDisabled: false, verifyDisabled: true }
    };
    this.baseOpportunityButtonStates = { searchDisabled: false, verifyDisabled: false };

    // Reset verification
    this.verifiedFields = {
      estimateId: false,
      accountName: false,
      shipToLocation: false,
      billTO: false,
      endUser: false
    };

    const hasEstimate = !!this.initialEstimateInfo?.estimateId;
    this.estimateButtonStates = { searchDisabled: hasEstimate, verifyDisabled: hasEstimate };
    this.verifiedFields.estimateId = hasEstimate;

    if (!hasEstimate) {
      this.disableFormExceptEstimate();
    }

    this.cancel.emit();
  }

  createOpportunity(): void {
    if (!this.canCreate || this.isCreating) {
      return;
    }

    this.isCreating = true;
    this.operationStatus = null;

    const c = this.createOpportunityFormGroup.controls;
    this.updateOutputModel();

    // Show spinner for the creation operation
    this.loadingSpinnerService.show(CONSTANTS.DASHBOARD_SPINNER);

    this.crmOperations.createOpportunity({
      estimateInfo: this.estimateInfoForOpportunity,
      opportunityName: c.opportunityName.value,
      opportunityDescription: c.opportunityDescription.value,
      expectedBook: c.expectedBook.value!,
      sellAs: c.sellAs.value,
      ecs: c.ecs.value,
      restrictionType: c.restrictionType.value,
      isChangeOrder: this.isChangeOrder,
      baseOpportunityId: c.baseOpportunity.value,
      baseOpportunity: this.isChangeOrder ? this.opportunityModel : null,
      soldToCustomerInfo: this.accountNameData,
      shipToCustomerInfo: this.shipToLocationData,
      billToCustomerInfo: this.billToData,
      endUserCustomerInfo: this.endUserData,
      federalFundedOutput: this.federalFundedOutput,
      userId: this.userProfileService.userInfo?.sam_account_name ?? '',
      userEmail: this.userProfileService.userInfo?.mail ?? ''
    })
      .pipe(takeUntil(this.destroy$), finalize(() => this.stopCreating()))
      .subscribe({
        next: (event: CreateOpportunityEvent) => {
          if (event.type === 'status') {
            this.operationStatus = event.status ?? null;
            this.loadingSpinnerService.updateMessage(event.status ?? '');
            return;
          }

          const response = event.result!;
          if (response.errorCode) {
            this.showCreateFailure(response.errorDescription);
          } else {
            this.crmOperations.syncEstimateDataIfOpen(response);
            this.notificationService.showSuccess(
              this.translate.instant('CREATE_OPPORTUNITY_SUCCESS', { oppName: c.opportunityName.value, oppId: response.opportunityId })
            );
          }
          this.cancel.emit();
        },
        error: async (error: unknown) => {
          if (await this.gocCollaborationRestrictionHandlerService.isGocUserRestricted(error)) {
            return;
          }

          this.showCreateFailure(this.extractErrorDetail(error));
        }
      });
  }

  // #endregion

  // #region ─── Private Methods ───

  /** Clears the in-progress state and the spinner once the creation stream terminates. */
  private stopCreating(): void {
    this.isCreating = false;
    this.operationStatus = null;
    this.loadingSpinnerService.updateMessage('');
    this.loadingSpinnerService.hide(CONSTANTS.DASHBOARD_SPINNER);
  }

  private showCreateFailure(message?: string): void {
    this.notificationService.showError(
      message || this.translate.instant('CREATE_OPPORTUNITY_FAILED')
    );
  }

  private extractErrorDetail(error: unknown): string | undefined {
    if (!(error instanceof HttpErrorResponse)) {
      return undefined;
    }

    const detail = error.error?.detail;
    return typeof detail === 'string' && detail.trim().length > 0 ? detail : undefined;
  }

  private initializeFormGroup(estimateInfo: EstimateInfoForOpportunity): void {
    const hasEstimate = !!(estimateInfo?.estimateId);

    let oppType = CONSTANTS.OPPORTUNITY_TYPE_BASE_ORDER;
    let estimateId = '';
    let opportunityName = '';
    let oppDescription = '';
    let salesOffice = '';
    let businessLine = '';
    let segment = '';
    let businessHierarchy = '';
    let expectedBook: Date | null = new Date();
    let sellAs = '';
    let isEcs = CONSTANTS.NO;

    if (hasEstimate) {
      oppType = estimateInfo.type === EstimateTypes.ChangeOrderEstimate
        ? CONSTANTS.OPPORTUNITY_TYPE_CHANGE_ORDER
        : CONSTANTS.OPPORTUNITY_TYPE_BASE_ORDER;
      estimateId = estimateInfo.estimateId;
      opportunityName = estimateInfo.estimateName;
      oppDescription = estimateInfo.description;
      salesOffice = estimateInfo.salesOffice;
      businessLine = estimateInfo.businessUnit;
      segment = estimateInfo.segment;
      businessHierarchy = estimateInfo.subSegment;
      sellAs = estimateInfo.totalSellPrice.toString();
      isEcs = estimateInfo.isECS ? CONSTANTS.YES : CONSTANTS.NO;
    }

    this.createOpportunityFormGroup = this.fb.group<ICreateOpportunityFormGroup>({
      opportunityType: this.fb.control(oppType),
      estimateId: this.fb.control({ value: estimateId, disabled: this.crmOperations.isEstimateOpened() }),
      baseOpportunity: this.fb.control(''),
      accountName: this.fb.control(''),
      shipToLocation: this.fb.control(''),
      billTO: this.fb.control(''),
      endUser: this.fb.control(''),
      opportunityName: this.fb.control(opportunityName),
      opportunityDescription: this.fb.control(oppDescription),
      salesOffice: this.fb.control(salesOffice),
      businessLine: this.fb.control(businessLine),
      segment: this.fb.control(segment),
      businessHierarchy: this.fb.control(businessHierarchy),
      expectedBook: this.fb.control<Date | null>(expectedBook),
      sellAs: this.fb.control(sellAs),
      ecs: this.fb.control({ value: isEcs, disabled: true }),
      restrictionType: this.fb.control(CONSTANTS.DEFAULT_RESTRICTION_TYPE),
      governmentEntity: this.fb.control(''),
      governmentAgency: this.fb.control(''),
      buyAmerican: this.fb.control(''),
      contractSchedule: this.fb.control(''),
      defensivePriorities: this.fb.control(''),
      cmmc: this.fb.control(''),
    });

    this.collaborativeControlDisabledStates = null;
    this.collaborativePreviousFormDisabled = null;
  }

  private async initializeInitialEstimateState(): Promise<void> {
    if (await this.shouldBlockEstimateAssociation(this.estimateInfoForOpportunity)) {
      this.cancelChanges();
      return;
    }

    this.initializeFormGroup(this.estimateInfoForOpportunity);
    this.subscribeToCustomerIdChanges();
    this.subscribeToEstimateIdChanges();
    this.applyBaseOpportunityState();

    this.subscribeToSharedDataChanges();

    if (this.estimateInfoForOpportunity?.estimateId) {
      this.estimateButtonStates = { searchDisabled: true, verifyDisabled: true };
      this.verifiedFields.estimateId = true;

      if (!this.isChangeOrder) {
        const otherMetaData = this.crmOperations.getOtherMetaData();
        if (otherMetaData) {
          this.populateCustomerDataFromOtherMetaData(otherMetaData);
        }
      }
    } else {
      this.disableFormExceptEstimate();
    }

    if(this.canShowFederalFundedSection){    
    this.crmOperations.getUSInfoDDLists()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.usInfoDDLists = response;
          this.initializeFederalFundedControls();
          this.subscribeToFederalFundedChanges();
        },
        error: () => {
          this.disableAllFederalFundedControls();
        }
      });
    }
    else{
      this.disableAllFederalFundedControls();
    }
  }

  private async initializeVerifiedEstimateState(info: EstimateInfoForOpportunity): Promise<void> {
    if (await this.shouldBlockEstimateAssociation(info)) {
      this.cancelChanges();
      return;
    }

    this.estimateInfoForOpportunity = info;

    // Re-enable all controls before reinitializing
    this.createOpportunityFormGroup.enable();
    this.isFormDisabled = false;
    this.createOpportunityFormGroup.controls.ecs.disable(); // ECS currently remains disabled.

    // Tear down existing form subscriptions before re-subscribing
    this.resubscribe$.next();

    this.initializeFormGroup(info);
    this.subscribeToCustomerIdChanges();
    this.subscribeToEstimateIdChanges();
    this.applyBaseOpportunityState();

    // Populate customer data from verified estimate's OtherMetaData (base orders only)
    if (!this.isChangeOrder) {
      const otherMetaData = this.crmOperations.getOtherMetaDataFromResponse();
      if (otherMetaData) {
        this.populateCustomerDataFromOtherMetaData(otherMetaData);
      }
    }

    // Mark estimate as verified and lock buttons
    this.verifiedFields.estimateId = true;
    this.estimateButtonStates = { searchDisabled: true, verifyDisabled: true };

    // Re-apply federal funded cascade if data was loaded
    if (this.usInfoDDLists) {
      this.initializeFederalFundedControls();
      this.subscribeToFederalFundedChanges();
    }
  }

  private async shouldBlockEstimateAssociation(estimateInfo: EstimateInfoForOpportunity | null | undefined): Promise<boolean> {
    if (!estimateInfo?.estimateId) {
      return false;
    }

    let messageKey: string | null = null;
    if (estimateInfo.status === EstimateStatus.Submitted) {
      messageKey = 'ESTIMATE_ASSOCIATION_SAP_SUBMITTED_MESSAGE';
    }

    if (estimateInfo.opportunityId?.trim()) {
      messageKey = 'ESTIMATE_ASSOCIATION_ALREADY_LINKED_MESSAGE';
    }

    if (!messageKey) {
      return false;
    }

    const dialogRef = this.dialog.open(NotificationMessageComponent, {
      width: CONSTANTS.DEFAULT_DIALOG_WIDTH,
      disableClose: true,
      data: {
        type: CONSTANTS.TYPE_INFO,
        title: this.translate.instant('ESTIMATE_ASSOCIATION_TITLE'),
        message: this.translate.instant(messageKey)
      }
    });

    await firstValueFrom(dialogRef.afterClosed());
    return true;
  }

  private subscribeToEstimateIdChanges(): void {
    this.createOpportunityFormGroup.controls.estimateId.valueChanges
      .pipe(takeUntil(merge(this.destroy$, this.resubscribe$)))
      .subscribe((estimateId: string) => {
        const controls = this.createOpportunityFormGroup.controls;

        this.estimateButtonStates.verifyDisabled = false;
        this.estimateButtonStates.searchDisabled = false;
        this.verifiedFields.estimateId = false;

        this.crmOperations.clearOpenEstimateResponse();
        this.estimateInfoForOpportunity = new EstimateInfoForOpportunity();

        this.accountNameData = new CustomerInformation();
        this.shipToLocationData = new CustomerInformation();
        this.billToData = new CustomerInformation();
        this.endUserData = new CustomerInformation();

        controls.accountName.setValue('', { emitEvent: false });
        controls.shipToLocation.setValue('', { emitEvent: false });
        controls.billTO.setValue('', { emitEvent: false });
        controls.endUser.setValue('', { emitEvent: false });
        controls.baseOpportunity.setValue('', { emitEvent: false });
        controls.opportunityName.setValue('', { emitEvent: false });
        controls.opportunityDescription.setValue('', { emitEvent: false });
        controls.salesOffice.setValue('', { emitEvent: false });
        controls.businessLine.setValue('', { emitEvent: false });
        controls.segment.setValue('', { emitEvent: false });
        controls.businessHierarchy.setValue('', { emitEvent: false });
        controls.expectedBook.setValue(new Date(), { emitEvent: false });
        controls.sellAs.setValue('', { emitEvent: false });
        controls.ecs.setValue(CONSTANTS.NO, { emitEvent: false });

        this.customerButtonStates = {
          accountName: { searchDisabled: false, verifyDisabled: true },
          shipToLocation: { searchDisabled: false, verifyDisabled: true },
          billTO: { searchDisabled: false, verifyDisabled: true },
          endUser: { searchDisabled: false, verifyDisabled: true }
        };
        this.baseOpportunityButtonStates = { searchDisabled: false, verifyDisabled: false };
        this.verifiedFields = {
          estimateId: false,
          accountName: false,
          shipToLocation: false,
          billTO: false,
          endUser: false
        };
      });
  }

  private applyBaseOpportunityState(): void {
    const controls = this.createOpportunityFormGroup.controls;
    this.isChangeOrder = controls.opportunityType.value === CONSTANTS.OPPORTUNITY_TYPE_CHANGE_ORDER;

    if (this.isChangeOrder) {
      this.baseOpportunityDisabled = false;
      controls.baseOpportunity.enable();
      this.subscribeToBaseOpportunityIdChanges();

      // Disable all customer account controls for change orders
      this.customerAccountsDisabled = true;
      controls.accountName.disable();
      controls.shipToLocation.disable();
      controls.billTO.disable();
      controls.endUser.disable();
    } else {
      this.baseOpportunityDisabled = true;
      controls.baseOpportunity.disable();
      controls.baseOpportunity.setValue('');

      // Enable customer account controls for base orders
      this.customerAccountsDisabled = false;
      controls.accountName.enable({ emitEvent: false });
      controls.shipToLocation.enable({ emitEvent: false });
      controls.billTO.enable({ emitEvent: false });
      controls.endUser.enable({ emitEvent: false });
    }

    this.applyCollaborativeReadOnlyState();
  }

  private applyCollaborativeReadOnlyState(): void {
    if (!this.createOpportunityFormGroup) {
      return;
    }

    const controls = this.createOpportunityFormGroup.controls;
    if (this.isCollaborativeReadOnly) {
      if (!this.collaborativeControlDisabledStates) {
        const disabledStates = new Map<keyof ICreateOpportunityFormGroup, boolean>();
        for (const controlName of Object.keys(controls) as Array<keyof ICreateOpportunityFormGroup>) {
          disabledStates.set(controlName, controls[controlName].disabled);
        }
        this.collaborativeControlDisabledStates = disabledStates;
        this.collaborativePreviousFormDisabled = this.isFormDisabled;
      }
      this.createOpportunityFormGroup.disable({ emitEvent: false });
      this.isFormDisabled = true;
      return;
    }

    if (!this.collaborativeControlDisabledStates) {
      return;
    }

    this.createOpportunityFormGroup.enable({ emitEvent: false });
    for (const [controlName, wasDisabled] of this.collaborativeControlDisabledStates) {
      if (wasDisabled) {
        controls[controlName].disable({ emitEvent: false });
      }
    }
    this.isFormDisabled = this.collaborativePreviousFormDisabled ?? false;
    this.collaborativeControlDisabledStates = null;
    this.collaborativePreviousFormDisabled = null;
  }

  private subscribeToCustomerIdChanges(): void {
    const controls = this.createOpportunityFormGroup.controls;
    const fields = [CONSTANTS.ACCOUNT_NAME_KEY, CONSTANTS.SHIP_TO_KEY, CONSTANTS.BILL_TO_KEY, CONSTANTS.END_USER_KEY] as const;

    for (const field of fields) {
      controls[field].valueChanges
        .pipe(takeUntil(merge(this.destroy$, this.resubscribe$)))
        .subscribe((value) => {
          this.customerButtonStates[field].verifyDisabled = !value?.trim();
          this.verifiedFields[field] = false;
        });
    }
  }

  private subscribeToBaseOpportunityIdChanges(): void {
    this.createOpportunityFormGroup.controls.baseOpportunity.valueChanges
      .pipe(takeUntil(merge(this.destroy$, this.resubscribe$)))
      .subscribe(() => {
        this.opportunityModel = new OpportunityModel();
        this.clearCustomerDataForBaseOpportunity();
        this.createOpportunityFormGroup.controls.ecs.setValue(CONSTANTS.NO, { emitEvent: false });
        this.createOpportunityFormGroup.controls.restrictionType.setValue(CONSTANTS.DEFAULT_RESTRICTION_TYPE, { emitEvent: false });
        this.baseOpportunityButtonStates = { searchDisabled: false, verifyDisabled: false };
      });
  }

  private disableFormExceptEstimate(): void {
    this.isFormDisabled = true;
    this.createOpportunityFormGroup.disable();
    // Re-enable the estimate ID field so the user can search/verify
    this.createOpportunityFormGroup.controls.estimateId.enable();
  }

  private initializeFederalFundedControls(): void {
    // 1. Populate entity dropdown and auto-select default
    this.entityItems = this.crmOperations.getEntityItems(this.usInfoDDLists);
    const defaultEntity = this.crmOperations.getDefaultEntity(this.usInfoDDLists);
    if (defaultEntity) {
      this.createOpportunityFormGroup.controls.governmentEntity.setValue(defaultEntity);
    }

    // 2-6. Apply cascading logic for the default entity
    this.applyEntityChange(this.createOpportunityFormGroup.controls.governmentEntity.value);
  }

  private subscribeToFederalFundedChanges(): void {
    // Government Entity changes
    this.createOpportunityFormGroup.controls.governmentEntity.valueChanges
      .pipe(takeUntil(merge(this.destroy$, this.resubscribe$)))
      .subscribe((entity) => {
        this.applyEntityChange(entity);
      });

    // Government Agency changes
    this.createOpportunityFormGroup.controls.governmentAgency.valueChanges
      .pipe(takeUntil(merge(this.destroy$, this.resubscribe$)))
      .subscribe((agency) => {
        this.onAgencyChange(agency);
      });

    // Contract Schedule changes
    this.createOpportunityFormGroup.controls.contractSchedule.valueChanges
      .pipe(takeUntil(merge(this.destroy$, this.resubscribe$)))
      .subscribe((schedule) => {
        this.onContractScheduleChange(schedule);
      });
  }

  private disableAllFederalFundedControls(): void {
    this.setControlDisabled('governmentEntity', true);
    this.setControlDisabled('governmentAgency', true);
    this.setControlDisabled('contractSchedule', true);
    this.setControlDisabled('buyAmerican', true);
    this.setControlDisabled('defensivePriorities', true);
    this.setControlDisabled('cmmc', true);

    this.buyAmericanState = { yesEnabled: false, noEnabled: false, checked: null };
    this.dpasState = { yesEnabled: false, noEnabled: false, checked: null };
    this.cmmcState = { cuiEnabled: false, fciEnabled: false, checked: null };
  }

  private applyEntityChange(entity: string): void {
    const data = this.usInfoDDLists;

    // 1. Re-filter Government Agency without auto-selecting.
    const agencyResult = this.crmOperations.getAgencyDropdownItems(data, entity);
    this.agencyItems = agencyResult.items;
    this.setControlDisabled('governmentAgency', agencyResult.disabled);
    this.createOpportunityFormGroup.controls.governmentAgency.setValue('', { emitEvent: false });

    // 2. Re-filter Contract Schedule without auto-selecting.
    const scheduleResult = this.crmOperations.getContractScheduleDropdownItems(data, entity);
    this.contractScheduleItems = scheduleResult.items;
    this.setControlDisabled('contractSchedule', scheduleResult.disabled);
    this.createOpportunityFormGroup.controls.contractSchedule.setValue('', { emitEvent: false });

    // 3. Re-evaluate Buy American
    this.buyAmericanState = this.crmOperations.getBuyAmericanState(data, entity);
    this.applyRadioState('buyAmerican', this.buyAmericanState);

    // 4. Re-evaluate DPAS
    this.dpasState = this.crmOperations.getDpasState(data, entity);
    this.applyRadioState('defensivePriorities', this.dpasState);

    // 5. Re-evaluate CMMC (entity only on entity change)
    this.cmmcState = this.crmOperations.getCmmcState(data, entity);
    this.applyCmmcState();

    // 6. Re-evaluate restriction type using empty selection until the user picks a schedule.
    this.applyRestrictionType();

    // Update output model
    this.updateOutputModel();
    this.applyCollaborativeReadOnlyState();
  }

  private onAgencyChange(agency: string): void {
    const entity = this.createOpportunityFormGroup.controls.governmentEntity.value;

    // Re-evaluate CMMC with entity AND agency
    this.cmmcState = this.crmOperations.getCmmcState(this.usInfoDDLists, entity, agency);
    this.applyCmmcState();

    this.updateOutputModel();
  }

  private onContractScheduleChange(_schedule: string): void {
    this.applyRestrictionType();
    this.updateOutputModel();
  }

  private applyRadioState(controlName: 'buyAmerican' | 'defensivePriorities', state: IRadioButtonState): void {
    const control = this.createOpportunityFormGroup.controls[controlName];
    if (!state.yesEnabled && !state.noEnabled) {
      this.setControlDisabled(controlName, true);
      control.setValue('');
    } else {
      this.setControlDisabled(controlName, false);
      control.setValue(state.checked ?? '');
    }
  }

  private applyCmmcState(): void {
    const control = this.createOpportunityFormGroup.controls.cmmc;
    if (!this.cmmcState.cuiEnabled && !this.cmmcState.fciEnabled) {
      this.setControlDisabled('cmmc', true);
      control.setValue('');
    } else {
      this.setControlDisabled('cmmc', false);
      control.setValue(this.cmmcState.checked ?? '');
    }
  }

  private applyRestrictionType(): void {
    const entity = this.createOpportunityFormGroup.controls.governmentEntity.value;
    const schedule = this.createOpportunityFormGroup.controls.contractSchedule.value;
    const result = this.crmOperations.getRestrictionTypeResult(this.usInfoDDLists, entity, schedule);
    this.restrictionTypeItems = result.restrictionTypes;

    const control = this.createOpportunityFormGroup.controls.restrictionType;
    const value = result.defaultRestriction?.toLowerCase() === CONSTANTS.RESTRICTED_TYPE
      ? CONSTANTS.RESTRICTED_TYPE
      : CONSTANTS.DEFAULT_RESTRICTION_TYPE;
    control.setValue(value, { emitEvent: false });

    if (result.isEditable) {
      this.setControlDisabled('restrictionType', false);
    } else {
      this.setControlDisabled('restrictionType', true);
    }
  }

  private setControlDisabled(controlName: keyof ICreateOpportunityFormGroup, disabled: boolean, emitEvent = true): void {
    const control = this.createOpportunityFormGroup.controls[controlName];
    if (this.isCollaborativeReadOnly) {
      this.collaborativeControlDisabledStates?.set(controlName, disabled);
      control.disable({ emitEvent });
      return;
    }

    if (disabled) {
      control.disable({ emitEvent });
    } else {
      control.enable({ emitEvent });
    }
  }

  private updateOutputModel(): void {
    const c = this.createOpportunityFormGroup.controls;
    this.federalFundedOutput = this.crmOperations.buildFederalFundedOutput({
      governmentEntity: c.governmentEntity.value,
      governmentAgency: c.governmentAgency.value,
      contractSchedule: c.contractSchedule.value,
      buyAmerican: c.buyAmerican.value,
      defensivePriorities: c.defensivePriorities.value,
      cmmc: c.cmmc.value,
      restrictionType: c.restrictionType.value
    });
  }

  private getCustomerControlValue(searchType: CustomerSearchType): string {
    const controls = this.createOpportunityFormGroup.controls;
    switch (searchType) {
      case CustomerSearchType.AccountName: return controls.accountName.value;
      case CustomerSearchType.ShipToLocation: return controls.shipToLocation.value;
      case CustomerSearchType.BillToLocation: return controls.billTO.value;
      case CustomerSearchType.EndUser: return controls.endUser.value;
      default: return '';
    }
  }

  private assignCustomerData(searchType: CustomerSearchType, customerInfo: CustomerInformation): void {
    const controls = this.createOpportunityFormGroup.controls;
    switch (searchType) {
      case CustomerSearchType.AccountName:
        this.accountNameData = customerInfo;
        controls.accountName.setValue(customerInfo.customerSalesforceId, { emitEvent: false });
        this.customerButtonStates[CONSTANTS.ACCOUNT_NAME_KEY] = { searchDisabled: false, verifyDisabled: true };
        this.verifiedFields.accountName = true;
        this.applySoldToAccountToCheckedSections();
        break;
      case CustomerSearchType.ShipToLocation:
        this.shipToLocationData = customerInfo;
        controls.shipToLocation.setValue(customerInfo.customerSalesforceId, { emitEvent: false });
        this.customerButtonStates[CONSTANTS.SHIP_TO_KEY] = { searchDisabled: false, verifyDisabled: true };
        this.applySoldToSection[CONSTANTS.SHIP_TO_KEY] = false;
        this.verifiedFields.shipToLocation = true;
        break;
      case CustomerSearchType.BillToLocation:
        this.billToData = customerInfo;
        controls.billTO.setValue(customerInfo.customerSalesforceId, { emitEvent: false });
        this.customerButtonStates[CONSTANTS.BILL_TO_KEY] = { searchDisabled: false, verifyDisabled: true };
        this.applySoldToSection[CONSTANTS.BILL_TO_KEY] = this.isBillToApplySoldToLocked;
        this.verifiedFields.billTO = true;
        break;
      case CustomerSearchType.EndUser:
        this.endUserData = customerInfo;
        controls.endUser.setValue(customerInfo.customerSalesforceId, { emitEvent: false });
        this.customerButtonStates[CONSTANTS.END_USER_KEY] = { searchDisabled: false, verifyDisabled: true };
        this.applySoldToSection[CONSTANTS.END_USER_KEY] = false;
        this.verifiedFields.endUser = true;
        break;
    }
  }

  private populateCustomerDataFromOtherMetaData(otherMetaData: OtherMetaData): void {
    if (otherMetaData.customerInformation?.customerSalesforceId) {
      this.assignCustomerData(CustomerSearchType.AccountName, otherMetaData.customerInformation);
    }
    if (otherMetaData.shipToAccount?.customerSalesforceId) {
      this.assignCustomerData(CustomerSearchType.ShipToLocation, otherMetaData.shipToAccount);
    }
    if (otherMetaData.billToAccount?.customerSalesforceId) {
      this.assignCustomerData(CustomerSearchType.BillToLocation, otherMetaData.billToAccount);
    }
    if (otherMetaData.endUserAccount?.customerSalesforceId) {
      this.assignCustomerData(CustomerSearchType.EndUser, otherMetaData.endUserAccount);
    }
  }

  private verifyBaseOpportunityForCreate(opportunityId: string): void {
    if (!this.estimateInfoForOpportunity?.estimateId) {
      return;
    }

    this.resetBaseOpportunityVerification();
    this.baseOpportunityButtonStates = { searchDisabled: false, verifyDisabled: true };

    const normalizedOpportunityId = opportunityId.trim().toUpperCase();
    if (normalizedOpportunityId.startsWith('SBT-') || normalizedOpportunityId.startsWith('OPP-')) {
      this.resetBaseOpportunityVerification();
      this.notificationService.showError(
        this.translate.instant('BASE_NOT_BASE_ERROR'),
        this.translate.instant('BASE_NOT_BOOKED_ERROR_CAP')
      );
      return;
    }

    const request: OpportunityRequest = {
      opportunityId,
      cultureId: this.cultureId
    };

    this.crmOperations.getOpportunity(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (opportunity) => {
          if (this.createOpportunityFormGroup.controls.baseOpportunity.value !== opportunityId) {
            return;
          }
          const sapJobNumber = opportunity.stSAPJobNumber?.trim();
          if (!opportunity.opportunityId || opportunity.isChangeOrder
            || !sapJobNumber || sapJobNumber.toLowerCase() === 'none') {
            this.resetBaseOpportunityVerification();
            const isUnbooked = !!opportunity.opportunityId && !opportunity.isChangeOrder
              && (!sapJobNumber || sapJobNumber.toLowerCase() === 'none');
            this.notificationService.showError(
              this.translate.instant(isUnbooked ? 'BASE_NOT_BOOKED_ERROR_MSG' : 'BASE_NOT_BASE_ERROR'),
              this.translate.instant('BASE_NOT_BOOKED_ERROR_CAP')
            );
            return;
          }

          this.opportunityModel = opportunity;
          this.createOpportunityFormGroup.controls.baseOpportunity.setValue(opportunity.opportunityId, { emitEvent: false });
          this.baseOpportunityButtonStates = { searchDisabled: true, verifyDisabled: true };
          this.createOpportunityFormGroup.controls.ecs.setValue(
            opportunity.bsesNationalAccts === CONSTANTS.Y ? CONSTANTS.YES : CONSTANTS.NO,
            { emitEvent: false }
          );
          if (opportunity.isRestrictedOpportunity === CONSTANTS.Y || opportunity.isRestrictedOpportunity === CONSTANTS.N) {
            this.createOpportunityFormGroup.controls.restrictionType.setValue(
              opportunity.isRestrictedOpportunity === CONSTANTS.Y ? CONSTANTS.RESTRICTED_TYPE : CONSTANTS.DEFAULT_RESTRICTION_TYPE,
              { emitEvent: false }
            );
          }
          this.populateCustomerDataFromAccounts(opportunity);
        },
        error: (error: unknown) => {
          if (this.createOpportunityFormGroup.controls.baseOpportunity.value !== opportunityId) {
            return;
          }
          this.resetBaseOpportunityVerification();
          this.notificationService.showError(
            this.translate.instant(error instanceof HttpErrorResponse && error.status === 404
              ? 'OPPORTUNITY_INFO_ID_INVALID_MSG'
              : 'OPPORTUNITY_CREATE_NEW_ENTER_DIFF_OPP_MSG'),
            this.translate.instant('OPPORTUNITY_CREATE_NEW_TITLE')
          );
        }
      });
  }

  private resetBaseOpportunityVerification(): void {
    this.opportunityModel = new OpportunityModel();
    this.clearCustomerDataForBaseOpportunity();
    this.baseOpportunityButtonStates = { searchDisabled: false, verifyDisabled: false };
  }

  private clearCustomerDataForBaseOpportunity(): void {
    const controls = this.createOpportunityFormGroup.controls;
    this.accountNameData = new CustomerInformation();
    this.shipToLocationData = new CustomerInformation();
    this.billToData = new CustomerInformation();
    this.endUserData = new CustomerInformation();
    controls.accountName.setValue('', { emitEvent: false });
    controls.shipToLocation.setValue('', { emitEvent: false });
    controls.billTO.setValue('', { emitEvent: false });
    controls.endUser.setValue('', { emitEvent: false });
    this.verifiedFields.accountName = false;
    this.verifiedFields.shipToLocation = false;
    this.verifiedFields.billTO = false;
    this.verifiedFields.endUser = false;
    this.customerButtonStates = {
      accountName: { searchDisabled: false, verifyDisabled: true },
      shipToLocation: { searchDisabled: false, verifyDisabled: true },
      billTO: { searchDisabled: false, verifyDisabled: true },
      endUser: { searchDisabled: false, verifyDisabled: true }
    };
    this.applySoldToSection = {
      [CONSTANTS.SHIP_TO_KEY]: false,
      [CONSTANTS.BILL_TO_KEY]: this.isBillToApplySoldToLocked,
      [CONSTANTS.END_USER_KEY]: false
    };
  }

  private populateCustomerDataFromAccounts(opportunity: OpportunityModel): void {
    this.clearCustomerDataForBaseOpportunity();
    const accounts = opportunity.accounts;

    const findAccount = (customerType: string | undefined) =>
      customerType ? accounts?.find((a: AccountModel) => a.customerTypeName === customerType) : undefined;

    const soldToAccount = findAccount(CONSTANTS.SOLD_TO);
    if (soldToAccount) {
      this.assignCustomerData(CustomerSearchType.AccountName, this.crmOperations.convertAccountToCustomerInfo(soldToAccount));
    } else if (opportunity.accountId || opportunity.soldToSAPNumber) {
      const soldTo = new CustomerInformation();
      soldTo.customerSalesforceId = opportunity.accountId ?? '';
      soldTo.customerID = opportunity.soldToSAPNumber ?? '';
      this.assignCustomerData(CustomerSearchType.AccountName, soldTo);
    }

    const shipToAccount = findAccount(CONSTANTS.SHIP_TO);
    if (shipToAccount) {
      this.assignCustomerData(CustomerSearchType.ShipToLocation, this.crmOperations.convertAccountToCustomerInfo(shipToAccount));
    }

    const billToAccount = findAccount(CONSTANTS.BILL_TO);
    if (billToAccount) {
      this.assignCustomerData(CustomerSearchType.BillToLocation, this.crmOperations.convertAccountToCustomerInfo(billToAccount));
    }

    const endUserAccount = findAccount(CONSTANTS.END_USER);
    if (endUserAccount) {
      this.assignCustomerData(CustomerSearchType.EndUser, this.crmOperations.convertAccountToCustomerInfo(endUserAccount));
    }
  }
  // #endregion

  //#region data binding helpers
  private subscribeToSharedDataChanges(): void {
    this.commonDataService.otherMetaDataChanged$.pipe(takeUntil(this.destroy$)).subscribe((sharedMetaData) => {
      if (this.isApplyingSharedData || !sharedMetaData || this.isChangeOrder) { return; }
      const isSameData =
        this.getCustomerSignature(sharedMetaData.customerInformation) === this.getCustomerSignature(this.accountNameData) &&
        this.getCustomerSignature(sharedMetaData.shipToAccount) === this.getCustomerSignature(this.shipToLocationData) &&
        this.getCustomerSignature(sharedMetaData.billToAccount) === this.getCustomerSignature(this.billToData) &&
        this.getCustomerSignature(sharedMetaData.endUserAccount) === this.getCustomerSignature(this.endUserData);
      if (isSameData) { return; }
      this.applySharedData(sharedMetaData)
    });
  }

  hasSoldToAccountSelected(): boolean {
    return !!this.accountNameData?.customerSalesforceId?.trim();
  }
  isApplySoldToChecked(section: string): boolean {
    return this.applySoldToSection[section] ?? false;
  }

  onApplySoldToChange(section: string, isChecked: boolean): void {
    this.applySoldToSection[section] = isChecked;
    if (isChecked && this.hasSoldToAccountSelected()) {
      this.assignSoldDataToSection(section);
    }
  }

  assignSoldDataToSection(section: string): void {
    const soldToData = this.cloneCustomerData(this.accountNameData);
    const controls = this.createOpportunityFormGroup.controls;
    switch (section) {
      case CONSTANTS.SHIP_TO_KEY:
        this.shipToLocationData = soldToData;
        controls.shipToLocation.setValue(soldToData.customerSalesforceId, { emitEvent: false });
        this.verifiedFields.shipToLocation = true;
        this.customerButtonStates[CONSTANTS.SHIP_TO_KEY] = { searchDisabled: false, verifyDisabled: true };
        break;
      case CONSTANTS.BILL_TO_KEY:
        this.billToData = soldToData;
        controls.billTO.setValue(soldToData.customerSalesforceId, { emitEvent: false });
        this.verifiedFields.billTO = true;
        this.customerButtonStates[CONSTANTS.BILL_TO_KEY] = { searchDisabled: false, verifyDisabled: true };
        break;
      case CONSTANTS.END_USER_KEY:
        this.endUserData = soldToData;
        controls.endUser.setValue(soldToData.customerSalesforceId, { emitEvent: false });
        this.verifiedFields.endUser = true;
        this.customerButtonStates[CONSTANTS.END_USER_KEY] = { searchDisabled: false, verifyDisabled: true };
        break;
    }
  }

  cloneCustomerData(source: CustomerInformation): CustomerInformation {
    return Object.assign(new CustomerInformation(), source);
  }
  private applySoldToAccountToCheckedSections(): void {
    if (!this.hasSoldToAccountSelected()) {
      this.applySoldToSection[CONSTANTS.SHIP_TO_KEY] = false;
      this.applySoldToSection[CONSTANTS.BILL_TO_KEY] = this.isBillToApplySoldToLocked;
      this.applySoldToSection[CONSTANTS.END_USER_KEY] = false;
      return;
    }
    if (this.applySoldToSection[CONSTANTS.SHIP_TO_KEY]) {
      this.assignSoldDataToSection(CONSTANTS.SHIP_TO_KEY);
    }
    if (this.applySoldToSection[CONSTANTS.BILL_TO_KEY]) {
      this.assignSoldDataToSection(CONSTANTS.BILL_TO_KEY);
    }
    if (this.applySoldToSection[CONSTANTS.END_USER_KEY]) {
      this.assignSoldDataToSection(CONSTANTS.END_USER_KEY);
    }
  }

  private getCustomerSignature(customerInfo: CustomerInformation | null | undefined): string {
    if (!customerInfo) {
      return '';
    }
    return `${customerInfo.customerSalesforceId}|${customerInfo.customerID}`;
  }

  private applySharedData(sharedData: OtherMetaData): void {
    this.isApplyingSharedData = true;
    const controls = this.createOpportunityFormGroup.controls;

    if (sharedData.customerInformation?.customerSalesforceId) {
      this.assignCustomerData(CustomerSearchType.AccountName, sharedData.customerInformation);
    } else {
      this.accountNameData = new CustomerInformation();
      controls.accountName.setValue('', { emitEvent: false });
      this.verifiedFields.accountName = false;
    }
    if (sharedData.shipToAccount?.customerSalesforceId) {
      this.assignCustomerData(CustomerSearchType.ShipToLocation, sharedData.shipToAccount);
    } else {
      this.shipToLocationData = new CustomerInformation();
      controls.shipToLocation.setValue('', { emitEvent: false });
      this.applySoldToSection[CONSTANTS.SHIP_TO_KEY] = false;
      this.verifiedFields.shipToLocation = false;
    }
    if (sharedData.billToAccount?.customerSalesforceId) {
      this.assignCustomerData(CustomerSearchType.BillToLocation, sharedData.billToAccount);
    } else {
      this.billToData = new CustomerInformation();
      controls.billTO.setValue('', { emitEvent: false });
      this.applySoldToSection[CONSTANTS.BILL_TO_KEY] = this.isBillToApplySoldToLocked;
      this.verifiedFields.billTO = false;
    }
    if (sharedData.endUserAccount?.customerSalesforceId) {
      this.assignCustomerData(CustomerSearchType.EndUser, sharedData.endUserAccount);
    } else {
      this.endUserData = new CustomerInformation();
      controls.endUser.setValue('', { emitEvent: false });
      this.applySoldToSection[CONSTANTS.END_USER_KEY] = false;
      this.verifiedFields.endUser = false;
    }

    this.isApplyingSharedData = false;
  }

  private publishSharedData(): void {
    if (this.isApplyingSharedData) { return; }
    const publishOtherMetaData = this.crmOperations.getOtherMetaData() ?? new OtherMetaData();
    publishOtherMetaData.customerInformation = this.cloneCustomerData(this.accountNameData);
    publishOtherMetaData.shipToAccount = this.cloneCustomerData(this.shipToLocationData);
    publishOtherMetaData.billToAccount = this.cloneCustomerData(this.billToData);
    publishOtherMetaData.endUserAccount = this.cloneCustomerData(this.endUserData);
    this.commonDataService.patchOtherMetaData(publishOtherMetaData);
  }
  //#endregion

  //#region PopUp Helpers
  showHoverCard = '';

  getPopupTitle(field: string): string {
    switch (field) {
      case CONSTANTS.ESTIMATE: return 'Estimate Details';
      case CONSTANTS.BASE_OPPORTUNITY: return 'Opportunity Details';
      case CONSTANTS.ACCOUNT_NAME_KEY: return 'Customer Details';
      case CONSTANTS.SHIP_TO_KEY: return 'Customer Details';
      case CONSTANTS.BILL_TO_KEY: return 'Customer Details';
      default: return '';
    }
  }

  getPopupRows(field: string): CRMEntityDetails[] {
    switch (field) {
      case CONSTANTS.ESTIMATE:
        return this.estimateInfoForOpportunity ? [
          { label: CONSTANTS.ID, value: this.estimateInfoForOpportunity.estimateId },
          { label: CONSTANTS.NAME, value: this.estimateInfoForOpportunity.estimateName },
          { label: CONSTANTS.DESCRIPTION, value: this.estimateInfoForOpportunity.description },
          { label: CONSTANTS.STATUS, value: this.estimateInfoForOpportunity.status?.toString() ?? '' },
          { label: CONSTANTS.TYPE, value: this.estimateInfoForOpportunity.type?.toString() ?? '' },
          { label: CONSTANTS.CUSTOMER, value: this.estimateInfoForOpportunity.customer },
          { label: CONSTANTS.AUTHOR, value: this.estimateInfoForOpportunity.author },
          { label: CONSTANTS.OWNER, value: this.estimateInfoForOpportunity.owner },
          { label: CONSTANTS.MODIFIED_BY, value: this.estimateInfoForOpportunity.modifiedBy },
          { label: CONSTANTS.USER_TYPE, value: this.estimateInfoForOpportunity.userType },
          { label: CONSTANTS.SEGMENT, value: this.estimateInfoForOpportunity.segment },
          { label: CONSTANTS.SALES_OFFICE, value: this.estimateInfoForOpportunity.salesOffice },
          { label: CONSTANTS.CREATION_DATE, value: this.multilingualDatePipe.transform(this.estimateInfoForOpportunity.createdDate, 'dateTimeWithSeconds', false) },
          { label: CONSTANTS.START_DATE, value: this.multilingualDatePipe.transform(this.estimateInfoForOpportunity.startDate, 'dateTimeWithSeconds', false) },
          { label: CONSTANTS.END_DATE, value: this.multilingualDatePipe.transform(this.estimateInfoForOpportunity.endDate, 'dateTimeWithSeconds', false) },
          { label: CONSTANTS.MODIFICATION_DATE, value: this.multilingualDatePipe.transform(this.estimateInfoForOpportunity.modifiedDate, 'dateTimeWithSeconds') },
          { label: CONSTANTS.LAST_UPLOAD_DATE, value: this.multilingualDatePipe.transform(this.estimateInfoForOpportunity.lastUploadDate, 'dateTimeWithSeconds') },
          { label: CONSTANTS.SAP_SUBMISSION_DATE, value: this.multilingualDatePipe.transform(this.estimateInfoForOpportunity.sapSubmissionDate, 'dateTimeWithSeconds') },
          { label: CONSTANTS.GM_PERCENT, value: this.estimateInfoForOpportunity.gmPercentage ? this.estimateInfoForOpportunity.gmPercentage.toString() : '' },
          { label: CONSTANTS.TOTAL_SELL_PRICE, value: this.estimateInfoForOpportunity.totalSellPrice ? this.estimateInfoForOpportunity.totalSellPrice.toString() : '' },
        ] : [];

      case CONSTANTS.BASE_OPPORTUNITY:
        return this.opportunityModel ? [
          { label: CONSTANTS.ID, value: this.opportunityModel.opportunityId },
          { label: CONSTANTS.NAME, value: this.opportunityModel.opportunityName },
          { label: CONSTANTS.SEGMENT, value: this.opportunityModel.plSegment },
          { label: CONSTANTS.BUSINESS_HIERARCHY, value: this.opportunityModel.plSubSegment },
          { label: CONSTANTS.SELL_AS, value: this.opportunityModel.cEstimateSalePrice },
          { label: CONSTANTS.EXPECTED_BOOK, value: this.multilingualDatePipe.transform(this.opportunityModel.dProjectEndDate, 'dateTime') },
        ] : [];

      case CONSTANTS.ACCOUNT_NAME_KEY:
        return this.getCustomerRows(this.accountNameData);
      case CONSTANTS.SHIP_TO_KEY:
        return this.getCustomerRows(this.shipToLocationData);
      case CONSTANTS.BILL_TO_KEY:
        return this.getCustomerRows(this.billToData);
      case CONSTANTS.END_USER_KEY:
        return this.getCustomerRows(this.endUserData);

      default: return [];
    }
  }

  private getCustomerRows(data: CustomerInformation): CRMEntityDetails[] {
    return [
      { label: CONSTANTS.SALESFORCE_ID, value: data.customerSalesforceId },
      { label: CONSTANTS.SAP_NUMBER, value: data.customerID },
      { label: CONSTANTS.NAME, value: data.customerName },
      { label: CONSTANTS.ADDRESS1, value: data.custAddress1 },
      { label: CONSTANTS.ADDRESS2, value: data.custAddress2 },
      { label: CONSTANTS.CITY, value: data.custCity },
      { label: CONSTANTS.STATE, value: data.custState },
      { label: CONSTANTS.ZIP_CODE, value: data.custZipCode },
    ];
  }

  canShowPopup(field: string): boolean {
    switch (field) {
      case CONSTANTS.ESTIMATE: return !!this.estimateInfoForOpportunity?.estimateId;
      case CONSTANTS.BASE_OPPORTUNITY: return !!this.opportunityModel?.opportunityId;
      case CONSTANTS.ACCOUNT_NAME_KEY: return !!this.accountNameData?.customerSalesforceId;
      case CONSTANTS.SHIP_TO_KEY: return !!this.shipToLocationData?.customerSalesforceId;
      case CONSTANTS.BILL_TO_KEY: return !!this.billToData?.customerSalesforceId;
      case CONSTANTS.END_USER_KEY: return !!this.endUserData?.customerSalesforceId;
      default: return false;
    }
  }
  //#endregion
}
